/**
 * Uni Email — React pages.
 *
 * The upstream project shipped these screens as Django templates with
 * hand-rolled fetch() calls into ``uni_email/views_api.py``.  Every
 * one of those templates was bound to a request-context user object
 * and to ``django.contrib.messages`` flashes — neither of which is
 * available inside a SPA.  This module is the ground-up React
 * rewrite: same screens, same model surface, but driven entirely by
 * ``/api/uni-email/*`` JSON.
 *
 * Pages exported:
 *   * UniEmailDashboard — landing page with KPIs + quick links.
 *   * UniEmailContacts  — paginated contacts list.
 *   * UniEmailLists     — list / segments.
 *   * UniEmailTemplates — reusable email templates.
 *   * UniEmailCampaigns — campaign list (read-mostly; sending lives
 *                         on the legacy service layer for now).
 *
 * Pages compose primitives from ``./UniShared.jsx`` (themed via
 * ``./uni.css``).  Tables are powered by the global ``.ty-table``
 * tokens so they look identical to Tasks / Complaints / Pharma lists.
 */
import React from 'react';
import {
  fmtCount, fmtPct, UniActionButton, UniBool, UniConfirmModal, UniKpiCard,
  UniPageShell, UniRowActions, UniSection, UniSpecEditModal, UniSpecViewModal,
  UniStatusPill, UniTable, UniTile, UniToolbar, uniSpecKeys, useUniDelete,
  useUniEdit, useUniList,
} from './UniShared.jsx';


/* ---------- Field specs --------------------------------------------- *
 * Drive both the Edit and the View modal for each list (see
 * ``UniSpecEditModal``).  Every key here is writable per
 * ``uni_email/api_serializers.py`` — engagement counters, rates and
 * lifecycle timestamps are ``read_only_fields`` there and so are marked
 * ``editable: false`` (shown in View, never PATCHed).  Choice lists
 * mirror ``uni_email/models.py``.                                      */

const CONTACT_STATUS = [
  { value: 'active',       label: 'Active' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
  { value: 'bounced',      label: 'Bounced' },
  { value: 'complained',   label: 'Complained' },
  { value: 'inactive',     label: 'Inactive' },
];

const TEMPLATE_CATEGORY = [
  { value: 'marketing',     label: 'Marketing' },
  { value: 'transactional', label: 'Transactional' },
  { value: 'newsletter',    label: 'Newsletter' },
  { value: 'welcome',       label: 'Welcome' },
  { value: 'followup',      label: 'Follow-up' },
  { value: 'promotional',   label: 'Promotional' },
  { value: 'notification',  label: 'Notification' },
  { value: 'other',         label: 'Other' },
];

const CAMPAIGN_STATUS = [
  { value: 'draft',     label: 'Draft' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'sending',   label: 'Sending' },
  { value: 'paused',    label: 'Paused' },
  { value: 'sent',      label: 'Sent' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'failed',    label: 'Failed' },
];

const CONTACT_FIELDS = [
  { key: 'email',  label: 'Email', required: true },
  { key: 'name',   label: 'Name' },
  { key: 'phone',  label: 'Phone' },
  { key: 'status', label: 'Status', type: 'select', options: CONTACT_STATUS },
  { key: 'tags',   label: 'Tags', hint: 'Comma-separated, e.g. vip, mumbai' },
  { key: 'source', label: 'Source', hint: 'How this contact was added: import, manual, crm…' },
  { key: 'emails_sent',    label: 'Emails sent',    editable: false, render: (r) => fmtCount(r.emails_sent) },
  { key: 'emails_opened',  label: 'Emails opened',  editable: false, render: (r) => fmtCount(r.emails_opened) },
  { key: 'emails_clicked', label: 'Emails clicked', editable: false, render: (r) => fmtCount(r.emails_clicked) },
  { key: 'open_rate',      label: 'Open rate',      editable: false, render: (r) => fmtPct(r.open_rate) },
  { key: 'click_rate',     label: 'Click rate',     editable: false, render: (r) => fmtPct(r.click_rate) },
  { key: 'bounce_count',   label: 'Bounces',        editable: false, render: (r) => fmtCount(r.bounce_count) },
];

const LIST_FIELDS = [
  { key: 'name',         label: 'List name', required: true },
  { key: 'description',  label: 'Description', type: 'textarea' },
  { key: 'icon',         label: 'Icon', hint: 'A single emoji shown beside the list name.' },
  { key: 'color',        label: 'Colour', hint: 'Hex value, e.g. #6366f1' },
  { key: 'is_active',    label: 'Active', type: 'check' },
  { key: 'is_public',    label: 'Allow public subscription', type: 'check' },
  { key: 'double_opt_in', label: 'Require double opt-in', type: 'check' },
  { key: 'member_count',        label: 'Members', editable: false, render: (r) => fmtCount(r.member_count) },
  { key: 'active_member_count', label: 'Active members', editable: false, render: (r) => fmtCount(r.active_member_count) },
];

const TEMPLATE_FIELDS = [
  { key: 'name',        label: 'Template name', required: true },
  { key: 'description', label: 'Description', type: 'textarea' },
  { key: 'category',    label: 'Category', type: 'select', options: TEMPLATE_CATEGORY },
  { key: 'subject',     label: 'Subject', required: true,
    hint: 'Supports {{name}} and {{email}} merge variables.' },
  { key: 'html_content', label: 'HTML body', type: 'textarea', rows: 8,
    hint: 'Also supports {{unsubscribe_url}}.' },
  { key: 'text_content', label: 'Plain-text body', type: 'textarea', rows: 4,
    hint: 'Auto-generated from the HTML when left blank.' },
  { key: 'is_active',   label: 'Active', type: 'check' },
  { key: 'is_system',   label: 'System template', editable: false },
  { key: 'used_count',  label: 'Times used', editable: false, render: (r) => fmtCount(r.used_count) },
];

const CAMPAIGN_FIELDS = [
  { key: 'name',        label: 'Campaign name', required: true },
  { key: 'description', label: 'Description', type: 'textarea' },
  { key: 'subject',     label: 'Subject', required: true },
  { key: 'from_name',   label: 'From name' },
  { key: 'from_email',  label: 'From email' },
  { key: 'reply_to',    label: 'Reply-to' },
  { key: 'status',      label: 'Status', type: 'select', options: CAMPAIGN_STATUS },
  { key: 'segment_tags', label: 'Only send to tags', hint: 'Comma-separated. Blank = the whole audience.' },
  { key: 'exclude_tags', label: 'Exclude tags', hint: 'Comma-separated.' },
  { key: 'track_opens',  label: 'Track opens', type: 'check' },
  { key: 'track_clicks', label: 'Track clicks', type: 'check' },
  { key: 'total_recipients', label: 'Recipients', editable: false, render: (r) => fmtCount(r.total_recipients) },
  { key: 'sent_count',       label: 'Sent',       editable: false, render: (r) => fmtCount(r.sent_count) },
  { key: 'delivered_count',  label: 'Delivered',  editable: false, render: (r) => fmtCount(r.delivered_count) },
  { key: 'opened_count',     label: 'Opens',      editable: false, render: (r) => fmtCount(r.opened_count) },
  { key: 'clicked_count',    label: 'Clicks',     editable: false, render: (r) => fmtCount(r.clicked_count) },
  { key: 'bounced_count',    label: 'Bounces',    editable: false, render: (r) => fmtCount(r.bounced_count) },
  { key: 'open_rate',        label: 'Open rate',  editable: false, render: (r) => fmtPct(r.open_rate) },
  { key: 'click_rate',       label: 'Click rate', editable: false, render: (r) => fmtPct(r.click_rate) },
];



/* ---------- Endpoint constants -------------------------------------- *
 * Kept local because they're only used here.  If we ever grow a
 * Uni-Email-specific axios client they should move into ``config.js``
 * next to the existing PHARMA_* keys.                                  */
const EP = {
  CONTACTS:  '/api/uni-email/contacts/',
  LISTS:     '/api/uni-email/lists/',
  TEMPLATES: '/api/uni-email/templates/',
  CAMPAIGNS: '/api/uni-email/campaigns/',
};

const THEME = 'email';


/* ====================================================================
   Dashboard
   ==================================================================== */

export function UniEmailDashboard() {
  // Four light list calls in parallel — each ``count`` is one KPI.
  // Cheaper than a dedicated ``/dashboard/`` endpoint and works
  // without any extra backend code.
  const contacts  = useUniList(EP.CONTACTS  + '?page=1&page_size=1');
  const lists     = useUniList(EP.LISTS     + '?page=1&page_size=1');
  const campaigns = useUniList(EP.CAMPAIGNS + '?page=1&page_size=1');
  const templates = useUniList(EP.TEMPLATES + '?page=1&page_size=1');

  const loading = contacts.loading || lists.loading || campaigns.loading || templates.loading;
  const v = (raw) => (loading ? '…' : fmtCount(raw?.count));

  return (
    <UniPageShell
      theme={THEME}
      icon="✉️"
      title="Email Marketing"
      subtitle="Bulk campaigns, contacts, templates and engagement analytics — all from one place."
      actions={
        <>
          <UniActionButton hero primary to="/uni-email/campaigns">📣 Campaigns</UniActionButton>
          <UniActionButton hero to="/uni-email/contacts">Contacts</UniActionButton>
          <UniActionButton hero to="/uni-email/templates">Templates</UniActionButton>
        </>
      }
    >
      <UniSection title="At a glance" />
      <div className="uni-kpis">
        {/* Each count links straight to the list behind it, so the
            dashboard answers "which ones?" in one click. */}
        <UniKpiCard icon="👥" label="Contacts"  value={v(contacts.raw)}  sublabel="subscribers"
                    to="/uni-email/contacts" />
        <UniKpiCard icon="📂" label="Lists"     value={v(lists.raw)}     sublabel="segments"
                    to="/uni-email/lists" />
        <UniKpiCard icon="📣" label="Campaigns" value={v(campaigns.raw)} sublabel="all-time"
                    to="/uni-email/campaigns" />
        <UniKpiCard icon="📝" label="Templates" value={v(templates.raw)} sublabel="reusable bodies"
                    to="/uni-email/templates" />

      </div>

      <UniSection title="Manage" />
      <div className="uni-tiles">
        <UniTile to="/uni-email/contacts"  icon="👥" title="Contacts"
                 sub="View, search and segment your subscribers" />
        <UniTile to="/uni-email/lists"     icon="📂" title="Lists & Segments"
                 sub="Group your audience for targeted campaigns" />
        <UniTile to="/uni-email/templates" icon="📝" title="Templates"
                 sub="Reusable email bodies with variables" />
        <UniTile to="/uni-email/campaigns" icon="📣" title="Campaigns"
                 sub="Drafts, scheduled and sent campaigns" />
      </div>
    </UniPageShell>
  );
}


/* ====================================================================
   Contacts
   ==================================================================== */

export function UniEmailContacts() {
  const [q, setQ] = React.useState('');
  const url = q ? `${EP.CONTACTS}?search=${encodeURIComponent(q)}` : EP.CONTACTS;
  const { rows, loading, error, raw, reload } = useUniList(url, [q]);
  const [viewing, setViewing] = React.useState(null);
  const edit = useUniEdit(EP.CONTACTS, () => reload());
  const del  = useUniDelete(EP.CONTACTS, () => reload());

  const columns = [
    { key: 'email', label: 'Email',
      render: (r) => <span style={{ fontWeight: 600 }}>{r.email}</span> },
    { key: 'name',  label: 'Name' },
    { key: 'phone', label: 'Phone' },
    { key: 'status', label: 'Status',
      render: (r) => <UniStatusPill value={r.status} /> },
    { key: 'emails_sent',    label: 'Sent',    align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.emails_sent)}</span> },
    { key: 'emails_opened',  label: 'Opened',  align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.emails_opened)}</span> },
    { key: 'emails_clicked', label: 'Clicked', align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.emails_clicked)}</span> },
    { key: 'actions', label: 'Actions', align: 'right',
      render: (r) => (
        <UniRowActions items={[
          { key: 'view', label: 'View', title: 'Full contact record and engagement',
            onClick: () => setViewing(r) },
          { key: 'edit', label: 'Edit', title: 'Edit this contact',
            onClick: () => edit.start(r, uniSpecKeys(CONTACT_FIELDS)) },
          { key: 'delete', label: 'Delete', danger: true,
            title: 'Permanently delete this contact',
            onClick: () => del.ask(r) },
        ]} />
      ) },
  ];

  return (
    <UniPageShell
      theme={THEME}
      icon="👥"
      title="Contacts"
      subtitle={raw?.count != null
        ? `${fmtCount(raw.count)} subscriber${raw.count === 1 ? '' : 's'}`
        : 'Subscribers and their engagement'}
      actions={<UniActionButton hero to="/uni-email">← Dashboard</UniActionButton>}
    >
      <UniToolbar onSearch={setQ} placeholder="Search by email or name…" />
      <UniTable
        rows={rows} loading={loading} error={error} columns={columns} rowKey="id"
        empty="No contacts yet"
        emptyHint="Import a CSV or sync subscribers via the API to see them here."
      />
      <UniSpecViewModal row={viewing} onClose={() => setViewing(null)}
                        title="Contact details" fields={CONTACT_FIELDS} />
      <UniSpecEditModal edit={edit} title="Edit contact" fields={CONTACT_FIELDS} />
      <UniConfirmModal state={del} title="Delete contact" confirmLabel="Delete contact">
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>
          Delete <strong>{del.row?.email}</strong> and their send history?
          This can't be undone.
        </p>
        {/* Deleting is the wrong tool for "stop emailing them": it loses
            the audit trail AND the suppression, so a re-import can start
            mailing them again.  Point at the status change instead. */}
        <div className="uni-note uni-note--warn" style={{ marginTop: 12 }}>
          <span aria-hidden="true">💡</span>
          <span>
            To stop emailing this person while keeping their history, set
            their status to <strong>Unsubscribed</strong> instead.
          </span>
        </div>
      </UniConfirmModal>
    </UniPageShell>
  );
}



/* ====================================================================
   Lists & Segments
   ==================================================================== */

export function UniEmailLists() {
  const { rows, loading, error, raw, reload } = useUniList(EP.LISTS);
  const [viewing, setViewing] = React.useState(null);
  const edit = useUniEdit(EP.LISTS, () => reload());
  const del  = useUniDelete(EP.LISTS, () => reload());

  const columns = [
    { key: 'name', label: 'List',
      render: (r) => <span style={{ fontWeight: 600 }}>{r.name}</span> },
    { key: 'description', label: 'Description',
      render: (r) => <span className="uni-num--muted">{r.description || '—'}</span> },
    { key: 'member_count', label: 'Members', align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.member_count)}</span> },
    { key: 'active_member_count', label: 'Active', align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.active_member_count)}</span> },
    { key: 'is_active', label: 'Active?', align: 'center',
      render: (r) => <UniBool value={r.is_active} /> },
    { key: 'is_public', label: 'Public', align: 'center',
      render: (r) => <UniBool value={r.is_public} /> },
    { key: 'actions', label: 'Actions', align: 'right',
      render: (r) => (
        <UniRowActions items={[
          { key: 'view', label: 'View', title: 'List settings and membership counts',
            onClick: () => setViewing(r) },
          { key: 'edit', label: 'Edit', title: 'Edit this list',
            onClick: () => edit.start(r, uniSpecKeys(LIST_FIELDS)) },
          { key: 'delete', label: 'Delete', danger: true,
            title: 'Delete this list (contacts are kept)',
            onClick: () => del.ask(r) },
        ]} />
      ) },
  ];
  return (
    <UniPageShell
      theme={THEME}
      icon="📂"
      title="Lists & Segments"
      subtitle={raw?.count != null
        ? `${fmtCount(raw.count)} list${raw.count === 1 ? '' : 's'}`
        : 'Group your audience for targeted campaigns'}
      actions={<UniActionButton hero to="/uni-email">← Dashboard</UniActionButton>}
    >
      <UniTable
        rows={rows} loading={loading} error={error} columns={columns} rowKey="id"
        empty="No lists yet"
        emptyHint="Create a list to start segmenting subscribers."
      />
      <UniSpecViewModal row={viewing} onClose={() => setViewing(null)}
                        title="List details" fields={LIST_FIELDS} />
      <UniSpecEditModal edit={edit} title="Edit list" fields={LIST_FIELDS} />
      {/* Membership is a through-model (``EmailListMember``), so dropping
          the list only removes the grouping — the contacts themselves
          survive.  Say so, or this reads far scarier than it is. */}
      <UniConfirmModal state={del} title="Delete list" confirmLabel="Delete list">
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>
          Delete the list <strong>{del.row?.name}</strong>?
          {del.row?.member_count > 0 && (
            <>
              {' '}Its <strong>{fmtCount(del.row.member_count)}</strong>{' '}
              member{del.row.member_count === 1 ? '' : 's'} stay in your
              contacts — only the grouping is removed.
            </>
          )}
          {' '}Campaigns already sent to it keep their stats.
        </p>
      </UniConfirmModal>
    </UniPageShell>
  );
}



/* ====================================================================
   Templates
   ==================================================================== */

export function UniEmailTemplates() {
  const { rows, loading, error, raw, reload } = useUniList(EP.TEMPLATES);
  const [viewing, setViewing] = React.useState(null);
  const edit = useUniEdit(EP.TEMPLATES, () => reload());
  const del  = useUniDelete(EP.TEMPLATES, () => reload());

  const columns = [
    { key: 'name', label: 'Name',
      render: (r) => <span style={{ fontWeight: 600 }}>{r.name}</span> },
    { key: 'subject', label: 'Subject',
      render: (r) => <span className="uni-num--muted">{r.subject || '—'}</span> },
    { key: 'category', label: 'Category',
      render: (r) => r.category ? <UniStatusPill value={r.category} palette="violet" /> : '—' },
    { key: 'used_count', label: 'Used', align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.used_count)}</span> },
    { key: 'is_active', label: 'Active?', align: 'center',
      render: (r) => <UniBool value={r.is_active} /> },
    // ``is_system`` templates are documented as "cannot be deleted"
    // (``uni_email/models.py``) — they back transactional mail the app
    // itself sends.  Hide Delete on those rather than offer a button
    // that either 500s or quietly breaks password-reset emails.
    { key: 'actions', label: 'Actions', align: 'right',
      render: (r) => (
        <UniRowActions items={[
          { key: 'view', label: 'View', title: 'Preview the subject and body',
            onClick: () => setViewing(r) },
          { key: 'edit', label: 'Edit', title: 'Edit this template',
            onClick: () => edit.start(r, uniSpecKeys(TEMPLATE_FIELDS)) },
          { key: 'delete', label: 'Delete', danger: true,
            show: !r.is_system,
            title: 'Permanently delete this template',
            onClick: () => del.ask(r) },
        ]} />
      ) },
  ];
  return (
    <UniPageShell
      theme={THEME}
      icon="📝"
      title="Email Templates"
      subtitle={raw?.count != null
        ? `${fmtCount(raw.count)} template${raw.count === 1 ? '' : 's'}`
        : 'Reusable email bodies with merge variables'}
      actions={<UniActionButton hero to="/uni-email">← Dashboard</UniActionButton>}
    >
      <UniTable
        rows={rows} loading={loading} error={error} columns={columns} rowKey="id"
        empty="No templates yet"
        emptyHint="Templates speed up campaign creation — save your common bodies here."
      />
      <UniSpecViewModal row={viewing} onClose={() => setViewing(null)}
                        title="Template details" fields={TEMPLATE_FIELDS} width={640} />
      <UniSpecEditModal edit={edit} title="Edit template"
                        fields={TEMPLATE_FIELDS} width={640} />
      <UniConfirmModal state={del} title="Delete template" confirmLabel="Delete template">
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>
          Delete the template <strong>{del.row?.name}</strong>?
          {del.row?.used_count > 0 && (
            <>
              {' '}It has been used <strong>{fmtCount(del.row.used_count)}</strong>{' '}
              time{del.row.used_count === 1 ? '' : 's'}; campaigns built from it
              keep their own copy of the content.
            </>
          )}
          {' '}This can't be undone.
        </p>
        <div className="uni-note uni-note--warn" style={{ marginTop: 12 }}>
          <span aria-hidden="true">💡</span>
          <span>
            To retire it without deleting, uncheck <strong>Active</strong> in
            Edit — it stays available to existing campaigns.
          </span>
        </div>
      </UniConfirmModal>
    </UniPageShell>
  );
}



/* ====================================================================
   Campaigns
   ==================================================================== */

export function UniEmailCampaigns() {
  const { rows, loading, error, raw, reload } = useUniList(EP.CAMPAIGNS);
  const [viewing, setViewing] = React.useState(null);
  const edit = useUniEdit(EP.CAMPAIGNS, () => reload());
  const del  = useUniDelete(EP.CAMPAIGNS, () => reload());
  const columns = [

    { key: 'name', label: 'Name',
      render: (r) => <span style={{ fontWeight: 600 }}>{r.name}</span> },
    { key: 'subject', label: 'Subject',
      render: (r) => <span className="uni-num--muted">{r.subject || '—'}</span> },
    { key: 'status', label: 'Status',
      render: (r) => <UniStatusPill value={r.status} /> },
    { key: 'total_recipients', label: 'Recipients', align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.total_recipients)}</span> },
    { key: 'sent_count', label: 'Sent', align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.sent_count)}</span> },
    { key: 'opened_count', label: 'Opens', align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.opened_count)}</span> },
    { key: 'clicked_count', label: 'Clicks', align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.clicked_count)}</span> },
    { key: 'open_rate', label: 'Open %', align: 'right',
      render: (r) => <span className="uni-num">{fmtPct(r.open_rate)}</span> },
    // A campaign mid-flight is the one row on these screens where a
    // careless click has real-world consequences (half an audience
    // mailed, the other half not).  Editing and deleting are therefore
    // withheld while ``status === 'sending'`` — the sender service owns
    // the row until it finishes.  Everything else stays available so a
    // sent campaign can still be renamed or archived.
    { key: 'actions', label: 'Actions', align: 'right',
      render: (r) => {
        const inFlight = r.status === 'sending';
        return (
          <UniRowActions items={[
            { key: 'view', label: 'View',
              title: 'Full campaign settings and delivery stats',
              onClick: () => setViewing(r) },
            { key: 'edit', label: 'Edit',
              show: !inFlight,
              title: 'Edit this campaign',
              onClick: () => edit.start(r, uniSpecKeys(CAMPAIGN_FIELDS)) },
            { key: 'delete', label: 'Delete', danger: true,
              show: !inFlight,
              title: 'Permanently delete this campaign and its stats',
              onClick: () => del.ask(r) },
          ]} />
        );
      } },
  ];
  return (
    <UniPageShell
      theme={THEME}
      icon="📣"
      title="Campaigns"
      subtitle={raw?.count != null
        ? `${fmtCount(raw.count)} campaign${raw.count === 1 ? '' : 's'}`
        : 'Drafts, scheduled and sent campaigns'}
      actions={<UniActionButton hero to="/uni-email">← Dashboard</UniActionButton>}
    >
      <UniTable
        rows={rows} loading={loading} error={error} columns={columns} rowKey="id"
        empty="No campaigns yet"
        emptyHint="Compose your first email campaign from a template or list."
      />
      <UniSpecViewModal row={viewing} onClose={() => setViewing(null)}
                        title="Campaign details" fields={CAMPAIGN_FIELDS} width={640} />
      <UniSpecEditModal edit={edit} title="Edit campaign"
                        fields={CAMPAIGN_FIELDS} width={640} />
      <UniConfirmModal state={del} title="Delete campaign" confirmLabel="Delete campaign">
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>
          Delete <strong>{del.row?.name}</strong>?
          {del.row?.sent_count > 0 && (
            <>
              {' '}This campaign already reached{' '}
              <strong>{fmtCount(del.row.sent_count)}</strong> recipient
              {del.row.sent_count === 1 ? '' : 's'}; deleting it also removes
              its open, click and bounce history.
            </>
          )}
          {' '}This can't be undone.
        </p>
        {del.row?.sent_count > 0 && (
          <div className="uni-note uni-note--warn" style={{ marginTop: 12 }}>
            <span aria-hidden="true">💡</span>
            <span>
              Sent campaigns are your delivery record — consider setting the
              status to <strong>Cancelled</strong> instead of deleting, so the
              reporting stays intact.
            </span>
          </div>
        )}
      </UniConfirmModal>
    </UniPageShell>
  );
}

